
import GenerateJigFeedbackActions, { FeedbackRating } from '@/components/modals/GenerateJigFeedbackActions.vue'
import GenerateJigFeedbackModal from '@/components/modals/GenerateJigFeedbackModal.vue'
import Notification from '@/components/helper/Notification.vue'
import { postSparkModalFeedback } from '@/services/ai-telemetry.service'
import { JigHelpers } from '@/utils/jig-helpers'
import IconPencil from '@svg/IconPencil.svg'
import IconUserGroup from '@svg/IconUserGroup.svg'
import { Component, Prop, Vue } from 'vue-property-decorator'

@Component({
  components: {
    GenerateJigFeedbackActions,
    GenerateJigFeedbackModal,
    IconPencil,
    IconUserGroup,
    Notification,
  },
})
export default class GenerateJigPreviewSection extends Vue {
  @Prop({ type: String, required: true })
  public previewUrl!: string

  @Prop({ type: String, required: true })
  public generatedJigId!: string

  @Prop({ type: String, default: '' })
  public editUrl!: string

  @Prop({ type: String, default: '' })
  public deeplinkUrl!: string

  @Prop({ type: String, default: '' })
  public warningCopy!: string

  // Feedback wiring. traceId is the Braintrust trace id captured from the
  // /ai/complete branch — when empty the thumbs are hidden because there is
  // nothing to attach feedback to.
  @Prop({ type: String, default: '' })
  public traceId!: string

  @Prop({ type: Number, default: -1 })
  public tenantId!: number

  @Prop({ type: String, default: '' })
  public userId!: string

  private previewIframeSrc: string = ''
  private previewIframeHeight: number = 440
  private submittedRating: FeedbackRating | null = null
  private pendingRating: FeedbackRating | null = null
  private showCommentPopup: boolean = false

  private get showFeedbackActions(): boolean {
    return this.traceId.length > 0
  }

  private get previewIframeStyle(): Partial<CSSStyleDeclaration> {
    return { height: this.previewIframeHeight + 'px' }
  }

  private mounted() {
    this.initializePreviewIframe()
  }

  private getPreviewIframeContainer(): HTMLElement | null {
    const refs = this.$refs as { previewIframeContainer?: HTMLElement | HTMLElement[] }
    const containerRef = refs.previewIframeContainer

    if (!containerRef) {
      return null
    }

    if (Array.isArray(containerRef)) {
      if (containerRef.length === 0) {
        return null
      }
      return containerRef[0]
    }

    return containerRef
  }

  private calculatePreviewIframeHeight(): number {
    const container = this.getPreviewIframeContainer()
    const width = container ? container.clientWidth : 0

    if (width <= 0) {
      return 0
    }

    const calculatedHeight = Math.round(width * 0.5625)
    return calculatedHeight < 440 ? 440 : calculatedHeight
  }

  private initializePreviewIframe() {
    this.previewIframeSrc = ''

    if (!this.previewUrl || this.previewUrl.length === 0) {
      return
    }

    const nextHeight = this.calculatePreviewIframeHeight()
    if (nextHeight <= 0) {
      return
    }

    this.previewIframeHeight = nextHeight

    const nextPreviewUrl = this.previewUrl

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.previewIframeSrc = nextPreviewUrl
      })
    })
  }

  private editInJigApp() {
    const target = JigHelpers.buildEditInAppUrl(this.generatedJigId) || this.editUrl || this.deeplinkUrl
    if (target) {
      window.open(target, '_blank')
    }
  }

  private shareWithTeam() {
    if (this.generatedJigId) {
      this.$router.push({ path: '/jig-detail/' + this.generatedJigId, query: { openActions: 'true' } })
    } else {
      this.$router.push('/jigs')
    }
    this.$emit('close')
  }

  private onFeedbackClick(rating: FeedbackRating) {
    if (this.submittedRating !== null) {
      return
    }
    this.pendingRating = rating
    this.showCommentPopup = true
  }

  private onFeedbackSubmit(payload: { comment: string }) {
    const rating = this.pendingRating
    if (rating === null) {
      return
    }
    this.submittedRating = rating
    this.pendingRating = null

    void postSparkModalFeedback({
      traceId: this.traceId,
      rating,
      comment: payload.comment,
      jigHashId: this.generatedJigId,
      tenantId: this.tenantId,
      userId: this.userId,
    })
  }

  private onRefreshClick() {
    this.$emit('refresh')
  }
}
